/*
 * Copyright 2025 Intuition Robotics ltd
 */
import {BaseDB_ApiGeneratorCaller} from "@intuitionrobotics/db-api-generator/frontend";
import {AlertStatus, CommentMap, DB_Alert, DB_Comment} from "@app/app-shared/alert";
import {DB_Object, PartialProperties} from "@intuitionrobotics/ts-common";
import {
	BaseHttpRequest
} from "@intuitionrobotics/thunderstorm";
import {ApiBinder_DBCreate} from "@intuitionrobotics/db-api-generator/shared/types";
import {upsertImpl} from "./utils";
import {
	ThunderDispatcher
} from "@intuitionrobotics/thunderstorm/frontend";
import {DefaultApiDefs} from "@intuitionrobotics/db-api-generator";
import {AlertsModule, dispatch_onAlertsUpdated} from "@modules/AlertsModule";

export interface OnCommentUpdated {
	__onCommentUpdated: (created: boolean, success: boolean, errorMessage?: string) => void
}

export const dispatch_onCommentUpdated = new ThunderDispatcher<OnCommentUpdated, "__onCommentUpdated">("__onCommentUpdated");

export interface OnCommentDeleted {
	__onCommentDeleted: () => void
}

export const dispatch_onCommentDeleted = new ThunderDispatcher<OnCommentDeleted, "__onCommentDeleted">("__onCommentDeleted");

class CommentsModule_Class
	extends BaseDB_ApiGeneratorCaller<DB_Comment<keyof CommentMap>> {

	private comments?: DB_Comment<keyof CommentMap>[];
	private addingCommentToAlert: { [alertId: string]: boolean | undefined } = {};

	constructor() {
		super({key: "comments", relativeUrl: "/v1/comments"}, "CommentsModule");
	}

	async onEntryCreated(response: DB_Comment<keyof CommentMap>): Promise<void> {
		this.upsertImpl(response);
		this.updateAlertTimeStamp(response);

		dispatch_onCommentUpdated.dispatchUI(true, true);
	}

	async onEntryDeleted(response: DB_Comment<keyof CommentMap>): Promise<void> {
		this.comments = this.comments?.filter(c => c._id !== response._id)
		this.updateAlertTimeStamp(response);

		dispatch_onCommentDeleted.dispatchUI();
	}

	// we dont update comments...
	// async onEntryUpdated(response: DB_Comment<keyof CommentMap>): Promise<void> {
	// 	this.upsertImpl(response);
	// 	await this.updateAlertTimeStamp(response);
	//
	// 	dispatch_onCommentUpdated.dispatchUI(false, true);
	// }

	// Concurrent error: when changing status, this function takes the previous alert with the older status
	private updateAlertTimeStamp(response: DB_Comment<keyof CommentMap>) {

		if (response.type === "feedback" || response.type === "statusChange") {
			dispatch_onAlertsUpdated.dispatchUI();
			return;
		}
		const alert = AlertsModule.getAllAlerts()?.find(a => a._id === response.alertId);

		if (!alert) {
			dispatch_onAlertsUpdated.dispatchUI();
			return;
		}

		alert._updated = response.timestamp
		AlertsModule.update(alert);
	}

	async onQueryReturned(response: DB_Comment<keyof CommentMap>[]): Promise<void> {
		this.comments = response;
		dispatch_onCommentUpdated.dispatchUI(false, true);
	}

	private upsertImpl = (item: DB_Comment<keyof CommentMap>) => {
		this.comments = this.comments || [];
		return upsertImpl(this.comments, item);
	};

	getComments = () => this.comments;

	getNumberOfComments(alertId: string) {
		return this.getCommentsByAlert(alertId)?.filter(c => c.type !== "statusChange" && c.type !== "feedback").length
	}

	getCommentsByAlert = (alertId: string) => {
		if (!this.comments)
			return [];

		return this.comments.filter(_comment => _comment.alertId === alertId);
	};

	// Can also accept an array of alert ids to check if adding comment to any of them.
	isAddingCommentToAlert = (alertId: string | string[]): boolean => {
		if (Array.isArray(alertId))
			return alertId.some(pId => !!this.addingCommentToAlert[pId]);

		return !!this.addingCommentToAlert[alertId];
	};

	create(body: PartialProperties<DB_Comment<keyof CommentMap>, "_id">): BaseHttpRequest<ApiBinder_DBCreate<DB_Comment<keyof CommentMap>>> {

		this.addingCommentToAlert[body.alertId] = true;

		// Force a re-render of components that should know about the start of the add comment.
		dispatch_onCommentUpdated.dispatchUI(false, true);

		return this
			.createRequest<ApiBinder_DBCreate<DB_Comment<keyof CommentMap>>>(DefaultApiDefs.Create)
			.setJsonBody(body)
			.setOnError((request: BaseHttpRequest<any>) => {
				const toLog = `Something went wrong while adding comment for alert ${body.alertId} `;
				this.logError(toLog);
				this.addingCommentToAlert[body.alertId] = false;
				dispatch_onCommentUpdated.dispatchUI(true, false, 'Error while adding comment');
			})
			.execute(async (response: DB_Comment<keyof CommentMap>) => {
				this.addingCommentToAlert[response.alertId] = false;
				return this.onEntryCreated(response);
			});
	}

	createFeedback(body: PartialProperties<DB_Comment<"feedback">, keyof DB_Object>, alert: DB_Alert<any>) {
		this.addingCommentToAlert[body.alertId] = true;

		// Force a re-render of components that should know about the start of the add comment.
		dispatch_onCommentUpdated.dispatchUI(false, true);

		return this
			.createRequest<ApiBinder_DBCreate<DB_Comment<keyof CommentMap>>>(DefaultApiDefs.Create)
			.setJsonBody(body)
			.setOnError((request: BaseHttpRequest<any>) => {
				const toLog = `Something went wrong while adding comment for alert ${body.alertId} `;
				this.logError(toLog);
				this.addingCommentToAlert[body.alertId] = false;
				dispatch_onCommentUpdated.dispatchUI(true, false, 'Error while adding comment');
			})
			.execute(async (response: DB_Comment<keyof CommentMap>) => {
				this.addingCommentToAlert[response.alertId] = false;
				await this.onEntryCreated(response);
				alert._updated = body.timestamp;
				alert.status = AlertStatus.COMPLETE;
				AlertsModule.upsertImpl(alert);
			});

	}
}

export const CommentsModule = new CommentsModule_Class();
